// Type for content property value structure
export type BylineContentPropertyValue = {
	title?: string;
	icon?: string;
	tooltip?: string;
};

// Utility function to check if a value is a non-empty string
const isNonEmptyString = (value: any): value is string => {
	return typeof value === 'string' && value.trim() !== '';
};

// Utility function to validate and set a byline content property value
const validatePropertyValue = (
	key: keyof BylineContentPropertyValue,
	value: any,
	validatedProperties: BylineContentPropertyValue,
) => {
	if (value !== undefined) {
		if (isNonEmptyString(value)) {
			validatedProperties[key] = value;
		} else {
			// eslint-disable-next-line no-console
			console.warn(`Byline content property ${key} must be a non-empty string, got:`, typeof value);
		}
	}
};

// Validation function for content property data
export const validateContentPropertyData = (
	data: any,
	defaultValues: { title: string; icon: string; tooltip: string; contentPropertyKey: string },
): { title: string; icon: string; tooltip: string; contentPropertyKey: string } => {
	try {
		// Check if data exists and has the expected structure
		if (
			!data ||
			!data.content ||
			!data.content.nodes ||
			data.content.nodes.length === 0 ||
			!data.content.nodes[0].properties ||
			!data.content.nodes[0].properties.nodes ||
			data.content.nodes[0].properties.nodes.length === 0
		) {
			// eslint-disable-next-line no-console
			console.warn('Invalid byline content property query data');
			return defaultValues;
		}

		// Get all objects that match the "-*ContentKey" pattern
		const properties = data.content.nodes[0].properties.nodes;
		const contentKeyPattern = new RegExp(`^forge-[a-f0-9-]+-${defaultValues.contentPropertyKey}$`);
		const matchingObjects = properties.filter((property: any) =>
			contentKeyPattern.test(property.key),
		);

		if (matchingObjects.length === 0) {
			// eslint-disable-next-line no-console
			console.warn('No property nodes matching content property key pattern found');
			return defaultValues;
		}

		if (matchingObjects.length > 1) {
			// eslint-disable-next-line no-console
			console.warn(
				'Multiple property nodes matching content property key pattern found. Please make sure your contentPropertyKey is unique, like a UUID.',
			);
			return defaultValues;
		}

		const propertyNode = matchingObjects[0];
		const validContentPropertyKey = propertyNode.key;

		if (!propertyNode.value) {
			// eslint-disable-next-line no-console
			console.warn('Byline content property data has no value');
			return defaultValues;
		}

		// Parse JSON with error handling
		let propertyValue: BylineContentPropertyValue;
		try {
			propertyValue = JSON.parse(propertyNode.value) as BylineContentPropertyValue;
		} catch (jsonError) {
			// eslint-disable-next-line no-console
			console.error('Failed to parse byline content property JSON:', jsonError);
			return defaultValues;
		}

		// Validate the structure of the parsed value
		if (typeof propertyValue !== 'object' || propertyValue === null) {
			// eslint-disable-next-line no-console
			console.error('Byline content property value is not an object:', propertyValue);
			return defaultValues;
		}

		const validatedProperties = { ...defaultValues };

		// Validate each property using the byline content property value utility function
		validatePropertyValue('title', propertyValue.title, validatedProperties);
		validatePropertyValue('icon', propertyValue.icon, validatedProperties);
		validatePropertyValue('tooltip', propertyValue.tooltip, validatedProperties);

		if (validContentPropertyKey && validContentPropertyKey !== '') {
			validatedProperties.contentPropertyKey = validContentPropertyKey;
		}

		return validatedProperties;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Unexpected error in content property validation:', error);
		return defaultValues;
	}
};
